import React, { useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';

import { useWizardNavigation } from './hooks/useWizard';
import { links } from './model/links';
import Styles from './OrganizationWizard.module.scss';
import { useFooterConfig } from './providers/FooterContext';
import { FooterContextProvider } from './providers/FooterContextProvider';
import {
	DefaultEditWizardStepState,
	DefaultWizardStepState,
	useWizardSteps,
} from './providers/WizardStepsContext';
import { WizardStepsProvider } from './providers/WizardStepsProvider';

import { Button } from '@/components/Elements/Button';
import { withDisable } from '@/components/Elements/DisableLink';
import { ArrowIcon, CloseIcon } from '@/components/Elements/Icons';
import { DeleteModal } from '@/components/Modals/DeleteModal';

const DisableMavLink = withDisable(NavLink);

export const OrganizationWizard = () => {
	const { id = '' } = useParams();
	const defaultStepState = !id ? DefaultWizardStepState : DefaultEditWizardStepState;

	return (
		<WizardStepsProvider defaultStepState={defaultStepState}>
			<FooterContextProvider>
				<div className={Styles.container}>
					<Header />
					<main>
						<Outlet />
					</main>
					<Footer />
				</div>
			</FooterContextProvider>
		</WizardStepsProvider>
	);
};

const Header = () => {
	const [shouldExit, setShouldExit] = React.useState(false);
	const { stepState, isLocked } = useWizardSteps();
	const { canGoBack, findPreviousStep } = useWizardNavigation();
	const navigate = useNavigate();
	const linkClassName = useCallback(({ isActive }: { isActive: boolean }) => {
		return isActive ? Styles.active : '';
	}, []);

	return (
		<header>
			{canGoBack() && (
				<NavLink
					className={Styles.backButton}
					type="button"
					to={findPreviousStep()?.link ?? '/'}
					replace
				>
					<ArrowIcon />
				</NavLink>
			)}
			<nav>
				{links.map((link, index) => (
					<React.Fragment key={index}>
						<DisableMavLink
							to={link.link}
							className={linkClassName}
							replace
							title={link.title}
							disabled={!stepState[link.key].isValid || isLocked}
						>
							{link.title}
						</DisableMavLink>
						{index !== links.length - 1 && <div className={Styles.separator} />}
					</React.Fragment>
				))}
			</nav>
			<NavLink
				to={'/organizations'}
				className={Styles.closeButton}
				onClick={(e) => {
					e.preventDefault();
					setShouldExit(true);
				}}
				replace
			>
				<CloseIcon />
			</NavLink>
			{shouldExit && (
				<DeleteModal
					cancelText="No"
					confirmText="Yes"
					title="Are you sure you wish to cancel the changes?"
					onCloseRequest={() => {
						setShouldExit(false);
					}}
					onClick={() => {
						navigate('/organizations', { replace: true });
					}}
				>
					<></>
				</DeleteModal>
			)}
		</header>
	);
};

const Footer = () => {
	const { config } = useFooterConfig();
	const {
		currentStep,
		isLastStep,
		canGoBack,
		findPreviousStep,
		findNextStep,
		moveBack,
		moveNext,
	} = useWizardNavigation();

	if (!currentStep || !config) {
		return null;
	}

	const previousStep = findPreviousStep();
	const nextStep = findNextStep();

	const {
		onCancel,
		onSubmit,
		isValid,
		isDirty,
		isLoading,
		isEnabled,
		shouldResubmit,
		isBackNavigationEnabled,
	} = config;

	const shouldDisplayEdit = shouldResubmit && isDirty && isEnabled;
	const nextDisabled = (isLoading ?? (shouldResubmit && isDirty)) || !isValid;
	const previousDisabled = (isLoading ?? !isBackNavigationEnabled) || (shouldResubmit && isDirty);
	const saveDisabled = (isLoading ?? !isDirty) || !isValid;
	const prevButtonVisible = canGoBack() && previousStep && isBackNavigationEnabled;

	return (
		<footer>
			<div className={Styles.navigation}>
				{shouldDisplayEdit && (
					<Button className={Styles.secondary} type={'button'} onClick={onCancel}>
						Cancel All Changes
					</Button>
				)}
				{prevButtonVisible && (
					<Button
						disabled={previousDisabled}
						className={Styles.secondary}
						type={'button'}
						onClick={moveBack}
					>
						Prev: {previousStep.title}
					</Button>
				)}
				{isEnabled && (
					<Button
						disabled={nextDisabled}
						type={'button'}
						onClick={async () => {
							await onSubmit?.();
							moveNext();
						}}
					>
						{nextStep
							? `Next: ${nextStep.title}`
							: isLastStep()
								? (currentStep.action ?? 'Create')
								: ''}
					</Button>
				)}
				{shouldDisplayEdit && (
					<Button disabled={saveDisabled} type={'button'} onClick={onSubmit}>
						Save
					</Button>
				)}
			</div>
		</footer>
	);
};
